import React, { useState, useMemo } from 'react';
import { Layout, Typography, Card, Row, Col, Pagination, Button, Tabs, Select, Input } from 'antd';
import { FaBookOpen, FaCheckCircle } from 'react-icons/fa';
import { SearchOutlined } from '@ant-design/icons';

const { Content } = Layout;
const { Title, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const learningData = {
    "AI & Machine Learning": [
        { title: "Intro to AI", description: "Learn the basics of AI and Machine Learning.", duration: "4 Weeks", level: "Beginner" },
        { title: "Deep Learning with TensorFlow", description: "Master deep learning concepts using TensorFlow.", duration: "6 Weeks", level: "Advanced" },
        { title: "AI Ethics", description: "Understand the ethical implications of AI.", duration: "3 Weeks", level: "Intermediate" },
        { title: "Natural Language Processing", description: "Explore NLP techniques and applications.", duration: "5 Weeks", level: "Intermediate" },
        { title: "AI for Robotics", description: "Learn AI techniques applied in robotics.", duration: "7 Weeks", level: "Advanced" },
        { title: "Computer Vision", description: "Learn to build vision-based AI applications.", duration: "6 Weeks", level: "Advanced" },
        { title: "Reinforcement Learning", description: "Understand and implement reinforcement learning algorithms.", duration: "5 Weeks", level: "Advanced" },
        { title: "AI in Healthcare", description: "Explore AI applications in healthcare.", duration: "4 Weeks", level: "Intermediate" },
        { title: "AI for Finance", description: "Learn AI applications in financial markets.", duration: "5 Weeks", level: "Intermediate" },
        { title: "AI for Agriculture", description: "Apply AI to optimize farming practices.", duration: "6 Weeks", level: "Advanced" }
    ],
    "Cybersecurity": [
        { title: "Ethical Hacking Basics", description: "Learn penetration testing fundamentals.", duration: "5 Weeks", level: "Intermediate" },
        { title: "Network Security", description: "Secure networks from cyber threats.", duration: "6 Weeks", level: "Advanced" },
        { title: "Cybersecurity for Beginners", description: "An introduction to cybersecurity principles.", duration: "4 Weeks", level: "Beginner" },
        { title: "Malware Analysis", description: "Analyze and detect malware in various systems.", duration: "6 Weeks", level: "Advanced" },
        { title: "Cloud Security", description: "Secure cloud platforms and services.", duration: "5 Weeks", level: "Intermediate" },
        { title: "Cybersecurity Incident Response", description: "Learn to respond to cybersecurity incidents.", duration: "4 Weeks", level: "Intermediate" },
        { title: "Cryptography Fundamentals", description: "Understand the basics of cryptography.", duration: "3 Weeks", level: "Beginner" },
        { title: "Web Application Security", description: "Secure web applications from common vulnerabilities.", duration: "5 Weeks", level: "Intermediate" },
        { title: "Security Operations Center (SOC) Analyst", description: "Learn to operate in a SOC environment.", duration: "6 Weeks", level: "Advanced" },
        { title: "Cybersecurity Risk Management", description: "Manage and mitigate cybersecurity risks.", duration: "4 Weeks", level: "Intermediate" }
    ],
    "Data Science": [
        { title: "Data Science Fundamentals", description: "Learn how to analyze data for insights.", duration: "4 Weeks", level: "Beginner" },
        { title: "SQL for Data Science", description: "Master SQL for handling databases.", duration: "3 Weeks", level: "Intermediate" },
        { title: "Big Data Analytics", description: "Analyze large datasets using Hadoop & Spark.", duration: "6 Weeks", level: "Advanced" },
        { title: "Data Visualization", description: "Learn how to visualize data effectively.", duration: "4 Weeks", level: "Intermediate" },
        { title: "Predictive Analytics", description: "Use statistical techniques for predictions.", duration: "6 Weeks", level: "Advanced" },
        { title: "Data Mining", description: "Extract valuable information from large datasets.", duration: "5 Weeks", level: "Advanced" },
        { title: "Machine Learning with Scikit-learn", description: "Build and deploy machine learning models.", duration: "6 Weeks", level: "Intermediate" },
        { title: "Data Science with Python", description: "Master data science using Python.", duration: "4 Weeks", level: "Intermediate" },
        { title: "Data Science with R", description: "Learn data science using R.", duration: "5 Weeks", level: "Intermediate" },
        { title: "Data Science Capstone Project", description: "Apply data science skills in a real-world project.", duration: "7 Weeks", level: "Advanced" }
    ],
    "Cloud Computing": [
        { title: "Cloud Fundamentals", description: "Learn the basics of cloud computing.", duration: "4 Weeks", level: "Beginner" },
        { title: "AWS Essentials", description: "Master the essential services on AWS.", duration: "5 Weeks", level: "Intermediate" },
        { title: "Google Cloud Platform", description: "Explore GCP services and architecture.", duration: "6 Weeks", level: "Advanced" },
        { title: "Azure Fundamentals", description: "Learn the basics of Microsoft Azure.", duration: "4 Weeks", level: "Beginner" },
        { title: "Cloud Security and Compliance", description: "Ensure security and compliance in the cloud.", duration: "5 Weeks", level: "Intermediate" },
        { title: "Cloud Architecture Design", description: "Design scalable and secure cloud architectures.", duration: "6 Weeks", level: "Advanced" },
        { title: "DevOps with Cloud", description: "Implement DevOps practices using cloud services.", duration: "5 Weeks", level: "Intermediate" },
        { title: "Serverless Computing", description: "Learn serverless architecture and its applications.", duration: "4 Weeks", level: "Intermediate" },
        { title: "Cloud Data Engineering", description: "Build data pipelines and manage data in the cloud.", duration: "6 Weeks", level: "Advanced" },
        { title: "Cloud Migration Strategies", description: "Plan and execute cloud migration projects.", duration: "5 Weeks", level: "Intermediate" }
    ],
    "Blockchain": [
        { title: "Blockchain Basics", description: "Introduction to blockchain technology.", duration: "4 Weeks", level: "Beginner" },
        { title: "Building Smart Contracts", description: "Learn how to build decentralized applications.", duration: "6 Weeks", level: "Advanced" },
        { title: "Cryptocurrency Fundamentals", description: "Understand the world of cryptocurrencies.", duration: "5 Weeks", level: "Intermediate" },
        { title: "Blockchain for Business", description: "Explore blockchain applications in business.", duration: "4 Weeks", level: "Intermediate" },
        { title: "Ethereum Development", description: "Develop applications on the Ethereum blockchain.", duration: "6 Weeks", level: "Advanced" },
        { title: "Blockchain Security", description: "Learn to secure blockchain applications.", duration: "5 Weeks", level: "Intermediate" },
        { title: "Hyperledger Fabric", description: "Develop enterprise blockchain solutions using Hyperledger.", duration: "6 Weeks", level: "Advanced" },
        { title: "Blockchain and IoT", description: "Integrate blockchain with IoT devices.", duration: "5 Weeks", level: "Intermediate" },
        { title: "Blockchain for Supply Chain", description: "Optimize supply chain management with blockchain.", duration: "4 Weeks", level: "Intermediate" },
        { title: "Blockchain for Healthcare", description: "Explore blockchain applications in healthcare.", duration: "6 Weeks", level: "Advanced" }
    ],
    "Programming": [
        { title: "Python for Beginners", description: "Learn the basics of Python programming.", duration: "4 Weeks", level: "Beginner" },
        { title: "Advanced Python", description: "Master advanced Python concepts and techniques.", duration: "6 Weeks", level: "Advanced" },
        { title: "JavaScript Fundamentals", description: "Learn the basics of JavaScript.", duration: "4 Weeks", level: "Beginner" },
        { title: "React for Web Development", description: "Build web applications using React.", duration: "5 Weeks", level: "Intermediate" },
        { title: "Node.js for Backend Development", description: "Develop backend applications using Node.js.", duration: "6 Weeks", level: "Advanced" },
        { title: "Java Programming", description: "Learn Java for enterprise applications.", duration: "4 Weeks", level: "Beginner" },
        { title: "C++ for Performance", description: "Master C++ for high-performance applications.", duration: "6 Weeks", level: "Advanced" },
        { title: "Swift for iOS Development", description: "Build iOS applications using Swift.", duration: "5 Weeks", level: "Intermediate" },
        { title: "Kotlin for Android Development", description: "Develop Android apps using Kotlin.", duration: "4 Weeks", level: "Intermediate" },
        { title: "Rust Programming", description: "Learn Rust for safe and concurrent programming.", duration: "6 Weeks", level: "Advanced" }
    ],
    "Web Development": [
        { title: "HTML & CSS Basics", description: "Learn the fundamentals of HTML and CSS.", duration: "3 Weeks", level: "Beginner" },
        { title: "Responsive Web Design", description: "Create responsive websites for all devices.", duration: "4 Weeks", level: "Intermediate" },
        { title: "JavaScript for Web", description: "Enhance web pages with JavaScript.", duration: "5 Weeks", level: "Intermediate" },
        { title: "React.js for Modern Web Apps", description: "Build modern web applications using React.js.", duration: "6 Weeks", level: "Advanced" },
        { title: "Angular for Enterprise Apps", description: "Develop enterprise-level web applications with Angular.", duration: "7 Weeks", level: "Advanced" },
        { title: "Vue.js for Dynamic Web Interfaces", description: "Create dynamic web interfaces with Vue.js.", duration: "5 Weeks", level: "Intermediate" },
        { title: "Web Development with Django", description: "Build web applications using Django.", duration: "6 Weeks", level: "Advanced" },
        { title: "Web Development with Flask", description: "Develop web applications using Flask.", duration: "4 Weeks", level: "Intermediate" },
        { title: "Web Security Best Practices", description: "Learn best practices for web security.", duration: "3 Weeks", level: "Intermediate" },
        { title: "Web Performance Optimization", description: "Optimize web applications for performance.", duration: "5 Weeks", level: "Advanced" }
    ],
    "Mobile Development": [
        { title: "Android Development with Kotlin", description: "Build Android apps using Kotlin.", duration: "6 Weeks", level: "Advanced" },
        { title: "iOS Development with Swift", description: "Develop iOS applications using Swift.", duration: "5 Weeks", level: "Intermediate" },
        { title: "Flutter for Cross-Platform Apps", description: "Create cross-platform mobile apps with Flutter.", duration: "6 Weeks", level: "Advanced" },
        { title: "React Native for Mobile Development", description: "Build mobile apps using React Native.", duration: "5 Weeks", level: "Intermediate" },
        { title: "Mobile App Design Principles", description: "Learn principles of mobile app design.", duration: "4 Weeks", level: "Intermediate" },
        { title: "Mobile App Testing and Debugging", description: "Test and debug mobile applications.", duration: "3 Weeks", level: "Intermediate" },
        { title: "Mobile Security", description: "Secure mobile applications from threats.", duration: "4 Weeks", level: "Intermediate" },
        { title: "Mobile App Monetization Strategies", description: "Learn strategies to monetize mobile apps.", duration: "3 Weeks", level: "Intermediate" },
        { title: "Mobile App Distribution", description: "Distribute mobile apps on various platforms.", duration: "4 Weeks", level: "Intermediate" },
        { title: "Advanced Mobile App Development", description: "Master advanced mobile app development techniques.", duration: "6 Weeks", level: "Advanced" }
    ],
    "DevOps": [
        { title: "DevOps Fundamentals", description: "Learn the basics of DevOps practices.", duration: "4 Weeks", level: "Beginner" },
        { title: "CI/CD Pipelines", description: "Set up continuous integration and deployment pipelines.", duration: "5 Weeks", level: "Intermediate" },
        { title: "Docker for Containerization", description: "Containerize applications using Docker.", duration: "4 Weeks", level: "Intermediate" },
        { title: "Kubernetes for Orchestration", description: "Orchestrate containers using Kubernetes.", duration: "6 Weeks", level: "Advanced" },
        { title: "Monitoring and Logging", description: "Implement monitoring and logging in DevOps.", duration: "5 Weeks", level: "Intermediate" },
        { title: "Infrastructure as Code", description: "Manage infrastructure using code.", duration: "4 Weeks", level: "Intermediate" },
        { title: "DevSecOps", description: "Integrate security into DevOps practices.", duration: "5 Weeks", level: "Intermediate" },
        { title: "Advanced DevOps", description: "Master advanced DevOps techniques and tools.", duration: "6 Weeks", level: "Advanced" },
        { title: "DevOps for Cloud Platforms", description: "Apply DevOps practices on cloud platforms.", duration: "5 Weeks", level: "Intermediate" },
        { title: "DevOps Capstone Project", description: "Apply DevOps skills in a real-world project.", duration: "7 Weeks", level: "Advanced" }
    ],
    "Data Engineering": [
        { title: "Data Engineering Fundamentals", description: "Learn the basics of data engineering.", duration: "4 Weeks", level: "Beginner" },
        { title: "Data Pipelines", description: "Build and manage data pipelines.", duration: "5 Weeks", level: "Intermediate" },
        { title: "ETL Processes", description: "Extract, transform, and load data efficiently.", duration: "4 Weeks", level: "Intermediate" },
        { title: "Data Warehousing", description: "Design and implement data warehouses.", duration: "6 Weeks", level: "Advanced" },
        { title: "Big Data Technologies", description: "Work with big data technologies like Hadoop and Spark.", duration: "5 Weeks", level: "Intermediate" },
        { title: "Data Lake Management", description: "Manage data lakes for large-scale data storage.", duration: "4 Weeks", level: "Intermediate" },
        { title: "Data Engineering with AWS", description: "Build data engineering solutions on AWS.", duration: "6 Weeks", level: "Advanced" },
        { title: "Data Engineering with GCP", description: "Work with data engineering tools on GCP.", duration: "5 Weeks", level: "Intermediate" },
        { title: "Data Engineering with Azure", description: "Implement data engineering solutions on Azure.", duration: "4 Weeks", level: "Intermediate" },
        { title: "Data Engineering Capstone Project", description: "Apply data engineering skills in a real-world project.", duration: "7 Weeks", level: "Advanced" }
    ],
    "Product Management": [
        { title: "Product Management Fundamentals", description: "Learn the basics of product management.", duration: "4 Weeks", level: "Beginner" },
        { title: "Product Strategy and Roadmapping", description: "Develop product strategies and roadmaps.", duration: "5 Weeks", level: "Intermediate" },
        { title: "User Research and Validation", description: "Conduct user research and validate product ideas.", duration: "4 Weeks", level: "Intermediate" },
        { title: "Agile Product Management", description: "Apply Agile methodologies in product management.", duration: "5 Weeks", level: "Intermediate" },
        { title: "Product Metrics and Analytics", description: "Measure and analyze product performance metrics.", duration: "4 Weeks", level: "Intermediate" },
        { title: "Product Launch and Growth", description: "Plan and execute product launches and growth strategies.", duration: "5 Weeks", level: "Intermediate" },
        { title: "Product Design and UX", description: "Collaborate with designers to create user-friendly products.", duration: "4 Weeks", level: "Intermediate" },
        { title: "Product Marketing and Positioning", description: "Develop product marketing and positioning strategies.", duration: "5 Weeks", level: "Intermediate" },
        { title: "Product Management for Startups", description: "Manage products in a startup environment.", duration: "4 Weeks", level: "Intermediate" },
        { title: "Product Management Capstone Project", description: "Apply product management skills in a real-world project.", duration: "7 Weeks", level: "Advanced" }
    ]
};

const pageSize = 4;

const LearningPage = () => {
  const [currentTab, setCurrentTab] = useState("AI & Machine Learning");
  const [currentPage, setCurrentPage] = useState(1);
  const [filterLevel, setFilterLevel] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const handleTabChange = (key) => {
    setCurrentTab(key);
    setCurrentPage(1);
    setFilterLevel('All');
    setSearchQuery('');
  };

  const handleLevelChange = (value) => {
    setFilterLevel(value);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const currentCourses = learningData[currentTab] || [];

  const filteredCourses = useMemo(() => {
    let filtered = currentCourses;
    if (filterLevel !== 'All') {
      filtered = filtered.filter((course) => course.level === filterLevel);
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (course) =>
          course.title.toLowerCase().includes(query) ||
          course.description.toLowerCase().includes(query)
      );
    }
    return filtered;
  }, [currentCourses, filterLevel, searchQuery]);

  const paginatedCourses = useMemo(() => {
    return filteredCourses.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  }, [filteredCourses, currentPage]);

  return (
    <Layout>
      <Content style={{ padding: '50px', background: 'linear-gradient(135deg, #f0f2f5, #e6f7ff)' }}>
        <Title level={2} style={{ fontSize: '20px', display: 'flex', alignItems: 'center', color: '#0050b3', marginBottom: '24px' }}>
          <FaBookOpen style={{ marginRight: '12px', fontSize: '20px' }} /> Explore Learning Resources
        </Title>
        <Paragraph style={{ fontSize: '16px', color: '#595959', marginBottom: '24px' }}>
          Learn AI, Data Science, and Cybersecurity through structured courses and hands-on exercises.
        </Paragraph>
        <div style={{ marginBottom: '24px', display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <Input
            placeholder="Search courses by title or description"
            prefix={<SearchOutlined />}
            style={{ width: '320px', borderRadius: '6px', height: '40px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
            onChange={handleSearchChange}
          />
          <Select
            defaultValue="All"
            style={{ width: '220px', borderRadius: '6px' }}
            onChange={handleLevelChange}
            dropdownStyle={{ borderRadius: '6px' }}
          >
            <Option value="All">All Levels</Option>
            <Option value="Beginner">Beginner</Option>
            <Option value="Intermediate">Intermediate</Option>
            <Option value="Advanced">Advanced</Option>
          </Select>
        </div>
        <Tabs
          defaultActiveKey="AI & Machine Learning"
          onChange={handleTabChange}
          className="custom-tabs"
          tabBarStyle={{ fontWeight: '600', color: '#0050b3', borderBottom: '2px solid #0050b3' }}
        >
          {Object.keys(learningData).map((category) => (
            <TabPane tab={category} key={category}>
              <Row gutter={[24, 24]}>
                {paginatedCourses.length > 0 ? (
                  paginatedCourses.map((course) => (
                    <Col xs={24} sm={12} md={8} lg={6} key={course.title}>
                      <Card
                        hoverable
                        style={{
                          borderRadius: '12px',
                          overflow: 'hidden',
                          boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
                          background: 'linear-gradient(135deg, #ffffff, #f9faff)',
                          height: '320px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          padding: '16px',
                          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-5px)';
                          e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.2)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.15)';
                        }}
                      >
                        <div>
                          <Title level={4} style={{ marginBottom: '12px', color: '#1a1a1a', fontSize: '18px', fontWeight: '600' }}>
                            {course.title}
                          </Title>
                          <Paragraph
                            style={{ color: '#595959', fontSize: '14px', marginBottom: '12px', lineHeight: '1.5' }}
                            ellipsis={{ rows: 3, expandable: false }}
                          >
                            {course.description}
                          </Paragraph>
                          <Paragraph style={{ marginBottom: '8px', color: '#595959', fontSize: '14px' }}>
                            <strong style={{ color: '#0050b3' }}>Duration:</strong> {course.duration}
                          </Paragraph>
                          <Paragraph style={{ marginBottom: '0', color: '#595959', fontSize: '14px' }}>
                            <strong style={{ color: '#0050b3' }}>Level:</strong>{' '}
                            <span
                              style={{
                                color:
                                  course.level === 'Beginner'
                                    ? '#52c41a'
                                    : course.level === 'Intermediate'
                                    ? '#fa8c16'
                                    : '#f5222d',
                                fontWeight: 'bold',
                              }}
                            >
                              {course.level}
                            </span>
                          </Paragraph>
                        </div>
                        <Button
                          type="primary"
                          icon={<FaCheckCircle style={{ marginRight: '8px' }} />}
                          style={{
                            background: 'linear-gradient(45deg, #0050b3, #003a8c)',
                            border: 'none',
                            borderRadius: '6px',
                            fontWeight: 'bold',
                            height: '40px',
                            width: '100%',
                            marginTop: '16px',
                            transition: 'all 0.3s ease',
                          }}
                          onMouseEnter={(e) => (e.target.style.background = 'linear-gradient(45deg, #003a8c, #002d62)')}
                          onMouseLeave={(e) => (e.target.style.background = 'linear-gradient(45deg, #0050b3, #003a8c)')}
                        >
                          Enroll Now
                        </Button>
                      </Card>
                    </Col>
                  ))
                ) : (
                  <Col span={24}>
                    <Paragraph style={{ textAlign: 'center', color: '#999', fontSize: '16px' }}>
                      No courses match your filters. Try adjusting your search or level selection!
                    </Paragraph>
                  </Col>
                )}
              </Row>
              <Pagination
                current={currentPage}
                total={filteredCourses.length}
                pageSize={pageSize}
                onChange={setCurrentPage}
                style={{ marginTop: '32px', textAlign: 'center', fontSize: '16px' }}
                showSizeChanger={false}
              />
            </TabPane>
          ))}
        </Tabs>
        {/* Custom CSS */}
        <style jsx global>{`
          .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
            color: #0050b3 !important;
          }
          .ant-tabs-ink-bar {
            background: #0050b3 !important;
          }
          .ant-pagination-item-active {
            border-color: #0050b3 !important;
          }
          .ant-pagination-item-active a {
            color: #0050b3 !important;
          }
        `}</style>
      </Content>
    </Layout>
  );
};

export default LearningPage;